import {
	ContainsDialogsWrapper,
	ContainsDropOversWrapper,
	IconButton,
	Portal
} from "client/Components";
import { ActivityFeed } from "client/Features/ActivityFeed/ActivityFeed";
import {
	PropsWithChildren,
	ReactElement,
	RefObject,
	useEffect,
	useLayoutEffect,
	useMemo,
	useRef,
	useState
} from "react";
import styles from "./ActivityMenu.module.less";
import {
	MediaQueryKind,
	useCallbackWithoutArguments,
	UsePointerEventOptions,
	usePointerEventOutside,
	useResizeObserver,
	useWorkspaceMediaQuery
} from "client/Hooks";
import { useResponsiveTitleStore } from "../../responsiveTitleStore";
import { usePageTitle } from "client/Hooks/usePageTitle";
import { localize } from "client_legacy/ndaw/culture";
import { useTitleBarNavigationStore } from "../../titleBarNavigationStore";
import { StackedStateCleanUp } from "client/Misc/stackedState";
import classNames from "classnames";
import { isNotNil, setTrackingId } from "client/Misc/utils";
import { ActivitiesInvocator } from "client/Features/Layout/MainMenuActions/ActivitiesInvocator";

export type ActivityMenuProps = {
	targetElement: HTMLElement;
	isSettingsOpen: boolean;
	onClose: () => void;
	onOpenSettings: () => void;
};

export const ActivityMenu = (props: ActivityMenuProps): ReactElement => {
	const {
		targetElement,
		isSettingsOpen,
		onClose,
		onOpenSettings: onOpenSettingsFromProps
	} = props;

	const [hasDialogOpen, setHasDialogOpen] = useState<boolean>(false);
	const [hasDropOverOpen, setHasDropOverOpen] = useState<boolean>(false);

	const onOpenSettings: () => void = useCallbackWithoutArguments(onOpenSettingsFromProps);
	const title = localize("_Activity");
	const isResponsive: boolean = useWorkspaceMediaQuery(MediaQueryKind.Responsive);

	const setBackNavigationCallback: (backNavigationCallback: (() => void)) => StackedStateCleanUp = useTitleBarNavigationStore(
		x => x.setBackNavigationCallback
	);

	useEffect(
		(): (() => void) => {
			return setBackNavigationCallback(onClose);
		},
		[
			onClose,
			setBackNavigationCallback
		]
	);

	const renderContent = (): ReactElement => {
		return (
			<div
				{...setTrackingId("activity-menu")}
				className={styles.content}
			>
				<div className={styles.header}>
					<h3 className={styles.title}>{title}</h3>
					<IconButton
						icon="gear"
						kind="transparent"
						onClick={onOpenSettings}
						shape="round"
						trackingId="open-notification-dialog-button"
					/>
					{!isResponsive && (
						<IconButton
							icon="xmark"
							kind="transparent"
							onClick={onClose}
							shape="round"
							trackingId="close-button"
						/>
					)}
				</div>
				<ContainsDialogsWrapper onDoesContainDialogsChange={setHasDialogOpen}>
					<ContainsDropOversWrapper onDoesContainDropOversChange={setHasDropOverOpen}>
						<ActivityFeed />
						<ActivitiesInvocator />
					</ContainsDropOversWrapper>
				</ContainsDialogsWrapper>
			</div>
		);
	};

	const host = useMemo(
		(): HTMLElement => {
			const element: HTMLElement | null = document
				.querySelector<HTMLElement>(".app-layout__columns");

			if (!element) {
				throw new Error("Host for activity menu is not found");
			}

			return element;
		},
		[]
	);

	const shouldDropoverStayOpen: boolean = hasDialogOpen
		|| hasDropOverOpen
		|| isSettingsOpen;

	return (
		<>
			{isResponsive && (
				<PageTitle
					title={title}
				/>
			)}

			<Portal container={host}>
				{isResponsive
					? (
						<div className={styles.responsiveContainer}>
							{renderContent()}
						</div>
					)
					: (
						<DropOverContainer
							targetElement={targetElement}
							onClose={
								!shouldDropoverStayOpen
									? onClose
									: undefined
							}
						>
							{renderContent()}
						</DropOverContainer>
					)
				}
			</Portal>
		</>
	);
};
ActivityMenu.displayName = "ActivityMenu";

type DropOverContainerProps = {
	targetElement: HTMLElement;
	onClose?: () => void;
};

const DropOverContainer = (props: PropsWithChildren<DropOverContainerProps>): ReactElement => {
	const {
		targetElement,
		onClose,
		children
	} = props;

	const [right, setRight] = useState<number | null>(null);

	const dropOverRef: RefObject<HTMLDivElement | null> = useRef<HTMLDivElement | null>(null);
	const { width: targetWidth } = useResizeObserver({ current: targetElement });
	const { width: dropOverWidth } = useResizeObserver(dropOverRef);

	useLayoutEffect(
		(): (() => void) | void => {
			// Avoid multiple calculations
			// and flickering issue
			const timeoutHandle: SetTimeoutHandle = setTimeout(
				(): void => {
					if (!dropOverRef.current) {
						throw new Error("Activity dropover ref is not defined");
					}

					const {
						right: targetRight
					} = targetElement.getBoundingClientRect();

					const pageWidth: number = document
						.body
						.clientWidth;

					const targetCenterFromRight: number = (pageWidth - targetRight) + (targetWidth / 2);
					const calculatedRight: number = Math.max(
						0,
						(targetCenterFromRight - (dropOverWidth / 2))
					);

					setRight(calculatedRight);
				},
				10
			);

			return (): void => {
				if (timeoutHandle) {
					clearTimeout(timeoutHandle);
				}
			};
		},
		[
			dropOverWidth,
			targetElement,
			targetWidth
		]
	);

	const options = useMemo(
		(): UsePointerEventOptions => ({
			ignoreWheelEvent: true
		}),
		[]
	);

	usePointerEventOutside(
		onClose,
		dropOverRef.current,
		options
	);

	return (
		<div
			ref={dropOverRef}
			className={classNames(
				styles.dropOverContainer,
				{ [styles.dropOverContainer_visible]: isNotNil(right) }
			)}
			style={{
				right: right ?? undefined
			}}
		>
			{children}
		</div>
	);
};
DropOverContainer.displayName = "DropOverContainer";

type PageTitleProps = {
	title: string;
};

const PageTitle = (props: PageTitleProps): null => {
	const { title } = props;
	const setCustomTitle: (customTitle: string) => (() => void) = useResponsiveTitleStore(x => x.setCustomTitle);

	usePageTitle(title);

	useEffect(
		() => {
			return setCustomTitle(title);
		},
		[
			setCustomTitle,
			title
		]
	);

	return null;
};
PageTitle.displayName = "PageTitle";